import { useInitialMonth } from './usePinnedOrderMonth.js';
/**
 * Статистика справочника: сколько чего сделали по месяцам.
 *
 * Отвечает на вопрос, которого нет ни в «Заказах», ни в «Финансах»: какие
 * аппараты идут, а какие лежат мёртвым грузом. В журнале это видно только
 * перелистыванием, а в финансах — одной суммой.
 *
 * Считается по ЗАКАЗАМ, а не по счетам: заказ — это сделанная работа, и
 * неоплаченный аппарат изготовлен ровно так же, как оплаченный. «Ожидание»
 * в счёт не идёт: работа заморожена и ещё не сделана.
 *
 * Доп. элементы считаются отдельно и со СВОИМ количеством: «Печать 3D
 * модели ×2» — это две печати, а не одна.
 */

import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatMonthKey, todayIso } from '@otc/shared';
import type { ApiConstruction, ApiOrder } from '../api/queries.js';
import { Spinner } from '../components/ui.js';

type Вид = 'base' | 'extra';

export function ConstructionStats({
  orders,
  constructions,
  загружается,
}: {
  orders: ApiOrder[] | undefined;
  constructions: ApiConstruction[];
  загружается: boolean;
}) {
  const [вид, setВид] = useState<Вид>('base');
  const initialMonth = useInitialMonth(todayIso());
  const [год, setГод] = useState(() => Number(initialMonth.slice(0, 4)));

  /*
   * ВСЕ ДВЕНАДЦАТЬ МЕСЯЦЕВ, даже будущие.
   *
   * Таблица так не перестраивается в течение года: в сентябре видно, что
   * октябрь впереди и пока пуст, а первого октября в нём просто появятся
   * числа. Иначе колонка возникала бы посреди года, и ширины столбцов
   * съезжали бы на глазах.
   */
  const месяцы = useMemo(
    () => Array.from({ length: 12 }, (_, i) => `${год}-${String(i + 1).padStart(2, '0')}`),
    [год],
  );

  /** Какие годы вообще есть в заказах — по ним и листаем. */
  const годы = useMemo(() => {
    const набор = new Set<number>([Number(todayIso().slice(0, 4))]);
    for (const o of orders ?? []) набор.add(Number(o.received.slice(0, 4)));
    return [...набор].sort((a, b) => b - a);
  }, [orders]);

  const таблица = useMemo(() => {
    const нужные = constructions.filter((к) =>
      вид === 'base' ? к.type === 'Базовая конструкция' : к.type === 'Дополнительный элемент',
    );
    const счёт = new Map<string, Map<string, number>>();
    const прибавить = (id: string, месяц: string, сколько: number) => {
      const строка = счёт.get(id) ?? new Map<string, number>();
      строка.set(месяц, (строка.get(месяц) ?? 0) + сколько);
      счёт.set(id, строка);
    };

    for (const o of orders ?? []) {
      /* Замороженный заказ ещё не сделан — в счёт не идёт. */
      if (o.status === 'Ожидание') continue;
      const месяц = o.received.slice(0, 7);
      if (!месяцы.includes(месяц)) continue;

      if (вид === 'base') {
        if (o.constructionId) прибавить(o.constructionId, месяц, o.constructionQty ?? 1);
      } else {
        for (const доп of o.extras ?? []) прибавить(доп.id, месяц, доп.qty);
      }
    }

    return нужные
      .map((к) => {
        const строка = счёт.get(к.id) ?? new Map<string, number>();
        const всего = [...строка.values()].reduce((s, v) => s + v, 0);
        return { id: к.id, name: к.name, поМесяцам: строка, всего };
      })
      /* Нулевые строки тоже показываются: «ни одного за год» — это ответ. */
      .sort((a, b) => b.всего - a.всего || a.name.localeCompare(b.name, 'ru'));
  }, [orders, constructions, вид, месяцы]);

  /* Соседние годы для стрелок: список идёт от новых к старым. */
  const местоГода = годы.indexOf(год);
  const раньше = местоГода >= 0 ? годы[местоГода + 1] : undefined;
  const позже = местоГода >= 0 ? годы[местоГода - 1] : undefined;

  if (загружается) return <Spinner label="Считаем…" />;

  const итогМесяца = (месяц: string) =>
    таблица.reduce((s, строка) => s + (строка.поМесяцам.get(месяц) ?? 0), 0);
  const итогГода = таблица.reduce((s, строка) => s + строка.всего, 0);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {(['base', 'extra'] as Вид[]).map((в) => (
          <button
            key={в}
            onClick={() => setВид(в)}
            aria-pressed={вид === в}
            className="rounded-full border px-3 py-1.5 text-[13px] transition-colors"
            /*
              Ненажатая — БЕЛАЯ с рамкой, нажатая — серой заливкой цвета темы.
              Прозрачная ненажатая читалась как подпись, а не как кнопка.
            */
            style={
              вид === в
                ? { background: 'var(--otc-accent)', borderColor: 'var(--otc-accent)', color: '#fff' }
                : {
                    background: 'var(--otc-surface)',
                    borderColor: 'var(--otc-line)',
                    color: 'var(--otc-ink)',
                  }
            }
          >
            {в === 'base' ? 'Аппараты' : 'Доп. элементы'}
          </button>
        ))}

        {/*
          Год — теми же стрелками и тем же списком, что в «Документах»:
          одинаковое действие должно выглядеть одинаково во всей программе.

          Единственный год не бледнеет: это не отключённое поле, а просто
          нечего выбирать.
        */}
        <span className="ml-auto flex items-center gap-2">
          <button
            onClick={() => раньше !== undefined && setГод(раньше)}
            disabled={раньше === undefined}
            className="otc-btn otc-btn-secondary otc-btn-icon"
            style={{ borderColor: 'var(--otc-line)' }}
            aria-label="Предыдущий год"
            title={раньше === undefined ? 'Раньше заказов нет' : `Перейти в ${раньше} год`}
          >
            <ChevronLeft size={15} className="text-ink2" />
          </button>
          <select
            className="rounded-lg border px-2 py-1 text-sm font-medium disabled:opacity-100"
            style={{
              borderColor: 'var(--otc-line)',
              background: 'var(--otc-surface)',
              color: 'var(--otc-ink)',
            }}
            disabled={годы.length < 2}
            value={год}
            onChange={(e) => setГод(Number(e.target.value))}
            aria-label="Год"
          >
            {годы.map((г) => (
              <option key={г} value={г}>
                {г} г.
              </option>
            ))}
          </select>
          <button
            onClick={() => позже !== undefined && setГод(позже)}
            disabled={позже === undefined}
            className="otc-btn otc-btn-secondary otc-btn-icon"
            style={{ borderColor: 'var(--otc-line)' }}
            aria-label="Следующий год"
            title={позже === undefined ? 'Позже заказов нет' : `Перейти в ${позже} год`}
          >
            <ChevronRight size={15} className="text-ink2" />
          </button>
        </span>
      </div>

      <div className="overflow-auto rounded-xl border" style={{ borderColor: 'var(--otc-line)' }}>
        <table className="w-full border-collapse">
          <thead>
            <tr style={{ background: 'var(--otc-table-head)' }}>
              <th className="sticky left-0 px-4 py-2 text-left text-xs font-semibold text-white"
                style={{ background: 'var(--otc-table-head)' }}
              >
                {вид === 'base' ? 'Конструкция' : 'Доп. элемент'}
              </th>
              {месяцы.map((м) => (
                <th key={м} className="px-3 py-2 text-right text-xs font-semibold text-white">
                  {/* Только месяц: год стоит в переключателе справа. */}
                  {formatMonthKey(м).replace(/\s\d{4}$/, '').slice(0, 3)}
                </th>
              ))}
              <th className="px-4 py-2 text-right text-xs font-semibold text-white">За год</th>
            </tr>
          </thead>
          <tbody>
            {таблица.map((строка, index) => (
              <tr
                key={строка.id}
                className={`otc-row ${index % 2 ? 'otc-row-alt' : ''}`}
                style={{ borderTop: '1px solid var(--otc-line)' }}
              >
                <td
                  className="sticky left-0 px-4 py-2 text-sm text-ink"
                  style={{ background: 'inherit' }}
                >
                  {строка.name}
                </td>
                {месяцы.map((м) => {
                  const сколько = строка.поМесяцам.get(м) ?? 0;
                  return (
                    <td
                      key={м}
                      className="tabular px-3 py-2 text-right text-sm"
                      style={{ color: сколько ? 'var(--otc-ink)' : 'var(--otc-line)' }}
                    >
                      {сколько || '—'}
                    </td>
                  );
                })}
                <td className="tabular px-4 py-2 text-right text-sm font-semibold text-ink">
                  {строка.всего || '—'}
                </td>
              </tr>
            ))}

            {/* Итог по месяцу — сколько всего изделий сделано. */}
            <tr style={{ borderTop: '2px solid var(--otc-line)' }}>
              <td
                className="sticky left-0 px-4 py-2 text-sm font-semibold text-ink"
                style={{ background: 'var(--otc-surface)' }}
              >
                Всего
              </td>
              {месяцы.map((м) => (
                <td key={м} className="tabular px-3 py-2 text-right text-sm font-semibold text-ink">
                  {итогМесяца(м) || '—'}
                </td>
              ))}
              <td className="tabular px-4 py-2 text-right text-sm font-semibold text-ink">
                {итогГода || '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  );
}
